import { domainCopy } from '../copy';
import type { Language } from '../types/language';

/**
 * An organisation's TAG — C18's 44px badge disc, the P09 inbox chip, O03's preview badge, a join code's
 * prefix. Evan's ruling, 2026-09-16: `initials` exists so the tag fits a disc; nothing else about an
 * organisation may stand in for it. apps/api mirrors this in src/domain/orgNames.ts (parity-tested).
 */

/** The disc holds four characters at most — the reason the field exists. */
export const ORG_INITIALS_MAX = 4;

/**
 * The first letters of the first two words, uppercased: "Boys & Girls Club of Decatur" → "BG". A word is a
 * run of letters or digits, so "&" is not one. The fallback for an organisation with no initials — it
 * replaces falling back to the full name, which put "Boys & Girls Club of Decatur" in a 44px circle.
 */
/**
 * What an organisation's initials are refused for, or null. Trimmed and uppercased first, so "  bgc " is the
 * three-letter tag its author meant rather than a six-character refusal.
 *
 * **The message says WHY**, because the natural mistake is to type the short name here: a 40-character value
 * in `initials` is the same failure as reusing `shortName` for the disc, only discovered later. It lives in
 * `@kk/domain` so the API and the mock refuse with the SAME sentence — a refusal whose wording differs
 * between backends is a difference a client can see.
 */
export function refuseOrgInitials(value: string, language?: Language): string | null {
  const tag = value.trim().toUpperCase();
  if (tag.length <= ORG_INITIALS_MAX) return null;
  return domainCopy(language).org.initialsTooLong(ORG_INITIALS_MAX, tag.slice(0, ORG_INITIALS_MAX), tag);
}

export function deriveOrgInitials(name: string): string {
  const words = name.match(/[\p{L}\p{N}]+/gu) ?? [];
  return words
    .slice(0, 2)
    .map((w) => w[0]!)
    .join('')
    .toUpperCase();
}

/** Every tag site reads this: the stored initials, else the derived ones. Never a name. */
export function orgTagOf(org: { initials?: string | null; name: string }): string {
  return org.initials || deriveOrgInitials(org.name);
}
